import { Injectable } from '@angular/core';
import { catchError, EMPTY, map } from 'rxjs';
import { Apollo } from 'apollo-angular';
import {
	GET_CLEARING_UNIVERSITY,
	GET_UNIVERSITY,
	GET_UNIVERSITY_SECTIONS,
	UPDATE_UNIVERSITY_GALLERY,
	UPDATE_UNIVERSITY_OVERVIEW,
} from '../queries.graphql';
import {
	ClearingUniversityResponse,
	GraphQLErrorCode,
	UniversityGallery,
	UniversityResponse,
	UniversitySectionsResponse,
	UpdateUniversityResponse,
} from '@uc/web/shared/data-models';
import { Router } from '@angular/router';
import { Product } from '@uc/shared/data-models';

@Injectable({
	providedIn: 'root',
})
export class UniversityApiService {
	constructor(
		private _apollo: Apollo,
		private _router: Router,
	) {}

	// GraphQL
	getUniversity(args: { universityUrl: string; product: Product }) {
		return this._apollo
			.query<UniversityResponse>({
				query: GET_UNIVERSITY,
				variables: {
					universityUrl: args.universityUrl,
					product: args.product.toUpperCase(),
				},
			})
			.pipe(
				catchError((error) => {
					if (error.networkError) {
						const code = error.networkError.error?.data?.code;
						if (code === GraphQLErrorCode.UNI_404) {
							this._router.navigate(['/universities']);
						}
					}
					return EMPTY;
				}),
				map(({ data }) => {
					return {
						...data.university,
						overview: data.universitySections.overview,
						universityGallery: data.universityGallery,
					};
				}),
			);
	}

	getClearingUniversity(uniSlug: string) {
		return this._apollo
			.query<ClearingUniversityResponse>({
				query: GET_CLEARING_UNIVERSITY,
				variables: {
					uniSlug: uniSlug,
				},
			})
			.pipe(
				catchError((error) => {
					if (error.networkError) {
						const code = error.networkError.error?.data?.code;
						if (code === GraphQLErrorCode.CLEARING_UNI_404) {
							this._router.navigate(['/universities/clearing']);
						}
					}
					return EMPTY;
				}),
				map(({ data }) => data.clearingUniversity),
			);
	}

	getUniversityOverview(args: { universityUrl: string; product: Product }) {
		return this._apollo
			.query<UpdateUniversityResponse>({
				query: UPDATE_UNIVERSITY_OVERVIEW,
				variables: {
					universityUrl: args.universityUrl,
					product: args.product.toUpperCase(),
				},
			})
			.pipe(
				catchError((error) => {
					console.error(error);
					return EMPTY;
				}),
				map(({ data }) => {
					return data.universitySections.overview;
				}),
			);
	}

	getUniversityGallery(args: { universityUrl: string; product: Product }) {
		return this._apollo
			.query<{ universityGallery: UniversityGallery }>({
				query: UPDATE_UNIVERSITY_GALLERY,
				variables: {
					universityUrl: args.universityUrl,
					product: args.product.toUpperCase(),
				},
			})
			.pipe(
				catchError((error) => {
					console.error(error);
					return EMPTY;
				}),
				map(({ data }) => {
					return data.universityGallery;
				}),
			);
	}

	getUniversitySections(args: { universityUrl: string; product: Product }) {
		return this._apollo
			.query<UniversitySectionsResponse>({
				query: GET_UNIVERSITY_SECTIONS,
				variables: {
					universityUrl: args.universityUrl,
					product: args.product?.toUpperCase(),
				},
			})
			.pipe(
				map(({ data }) => {
					return data.universitySections;
				}),
			);
	}
}
